import { FC } from "react";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import cx from "apps/website/utils/misc/cx";

import Image, { IImageProps } from "../../../base/Image/Image";
import SimpleTag, {
  ISimpleTag,
} from "../../../base/Tag/v2/SimpleTag/SimpleTag";
import TextBody from "../../../base/Text/TextBody/TextBody";
import TextSubtitle from "../../../base/Text/TextSubtitle/TextSubtitle";
import { IPillProps, Pill } from "../../../base/Pill/Pill";

const recipeCardImageAspectClassMap = {
  default: "lg:aspect-[1.8] aspect-[1.3]",
  narrow: "lg:aspect-[1.4] aspect-[1.3]",
} as const;

interface IStyleOptions {
  bgColor?: Theme;
  imageBgColor?: Theme;
  imageScale?: number;
  hasShadow?: boolean;
  imageAspect?: keyof typeof recipeCardImageAspectClassMap;
}

export interface IRecipeCard {
  titleTag: ISimpleTag;
  description: string;
  image: IImageProps;
  quantity?: number;
  imageBadge?: IPillProps;
  badgeList?: IPillProps[];
  styleOptions?: IStyleOptions;
}

export const RecipeCard: FC<IRecipeCard> = ({
  description,
  quantity,
  image,
  imageBadge,
  badgeList,
  titleTag,
  styleOptions }) => {
  const {
    bgColor = "default",
    imageBgColor = "light",
    imageScale = 100,
    hasShadow = false,
    imageAspect = "default",
  } = styleOptions || {};

  return (
    <div
      className={cx(
        "flex flex-col gap-7 w-full h-full",
        themeRootClassMap[bgColor],
        hasShadow && "shadow-lg rounded-md",
      )}
      data-component={RecipeCard.name}
    >
      <div className={cx("relative", recipeCardImageAspectClassMap[imageAspect])}>
        <div className="absolute inset-0 overflow-hidden">
          <Image
            image={{ src: image.src, width: image.width ?? 600, height: image.height ?? 400 }}
            alt={titleTag.label}
            cover
            objectPosition="object-center"
            imageClassName={`${themeRootClassMap[imageBgColor]} scale-${imageScale}`}
          />
        </div>

        { imageBadge && (
          <div data-testid="image-badge" className="absolute top-4 left-4">
            <Pill {...imageBadge} styleOptions={{ maxWidth: "fit", bgColor: "chartreuse-100" }} />
          </div>
        ) }

        <div className="flex gap-2 justify-between px-4 absolute -bottom-4 w-full">
          <SimpleTag
            {...titleTag}
            size={1}
            rotation={titleTag?.rotation ?? -1}
            bgColor="dark"
            textColor="secondary"
          />
          { quantity !== undefined && (<div data-testid="quantity-tag" className="bg-white rounded-full px-4 py-2 border-2 border-black ">
            <TextSubtitle size={3}>x{ quantity }</TextSubtitle>
          </div>) }
        </div>
      </div>

      <div className="flex flex-col gap-2 m-4">
        <TextBody size={3}>{ description }</TextBody>

        { badgeList?.length && (
          <div data-testid="badge-list" className="flex flex-col gap-2">
            { badgeList.map((badge) => (
              <Pill key={badge.label} {...badge} styleOptions={{ maxWidth: "fit", bgColor: "mid-green" }} />
            )) }
          </div>
        ) }
      </div>

    </div>
  );
};
